import React from "react";
import { Warning } from "@phosphor-icons/react";
import { C, YEARS, fmt, pct, fyl } from "./shared";

const CUSTOMER_ROWS = [
  ["new_customers", "New customers won", fmt],
  ["cac", "CAC — cost to acquire one", fmt],
  ["monthly_rev_per_customer", "Monthly revenue / customer", fmt],
  ["ltv", "LTV — lifetime value", fmt],
  ["ltv_cac", "LTV : CAC", (v) => (v == null ? "–" : `${v}×`)],
  ["payback_months", "CAC payback (months)", (v) => (v == null ? "–" : v)],
  ["gross_margin", "Gross margin", pct],
];
const UNIT_ROWS = [
  ["units", "Units sold", fmt],
  ["price_per_unit", "Avg price / unit", fmt],
  ["cost_per_unit", "Direct cost / unit", fmt],
  ["contribution_per_unit", "Contribution / unit", fmt],
  ["contribution_margin", "Contribution margin", pct],
  ["breakeven_units_month", "Breakeven units / month", fmt],
  ["avg_units_month", "Planned units / month", fmt],
];

const ratioTone = (v) => (v == null ? undefined : v >= 3 ? C.green : v >= 1 ? C.orange : C.red);

export default function UnitEconomics({ out }) {
  const ue = out.unit_economics;
  const yl = fyl(out);
  const f1 = ue.years.f1 || {};
  const rows = ue.mode === "customer" ? CUSTOMER_ROWS : UNIT_ROWS;
  return (
    <div data-testid="fm-unit-economics" className="max-w-4xl space-y-4">
      <div className="text-xs text-white/45">
        Section H · Unit economics — {ue.mode === "customer"
          ? "does one customer make you money, and how fast do you earn back the cost of winning them?"
          : ue.mode === "unit"
            ? "does one unit make you money, and how many do you need to sell to cover fixed costs?"
            : "blended streams — margin economics per stream are consolidated."}
      </div>
      {ue.warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-2 text-sm rounded-md px-4 py-3" data-testid={`fm-ue-warning-${i}`}
          style={{ backgroundColor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.4)", color: "#FCD9A0" }}>
          <Warning size={16} style={{ color: C.orange, marginTop: 2 }} weight="fill" /> {w}
        </div>
      ))}
      {ue.mode === "customer" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[[`CAC (${yl[0]})`, f1.cac == null ? "–" : fmt(f1.cac), undefined, "fm-ue-cac"],
            [`LTV (${yl[0]})`, fmt(f1.ltv), undefined, "fm-ue-ltv"],
            ["LTV : CAC", f1.ltv_cac == null ? "–" : `${f1.ltv_cac}×`, ratioTone(f1.ltv_cac), "fm-ue-ratio"],
            ["Payback", f1.payback_months == null ? "–" : `${f1.payback_months} mo`, f1.payback_months > 18 ? C.red : C.green, "fm-ue-payback"],
          ].map(([l, v, tone, tid]) => (
            <div key={l} className="rounded-lg px-4 py-3" data-testid={tid} style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.12)" }}>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{l}</div>
              <div className="text-lg font-extrabold mt-0.5" style={{ color: tone || "#fff" }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {ue.mode === "unit" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[[`Contribution / unit (${yl[0]})`, fmt(f1.contribution_per_unit), f1.contribution_per_unit > 0 ? C.green : C.red, "fm-ue-contrib"],
            ["Contribution margin", pct(f1.contribution_margin), undefined, "fm-ue-cm"],
            ["Breakeven units / mo", fmt(f1.breakeven_units_month), undefined, "fm-ue-breakeven"],
            ["Planned units / mo", fmt(f1.avg_units_month),
              f1.breakeven_units_month != null && f1.avg_units_month >= f1.breakeven_units_month ? C.green : C.orange, "fm-ue-planned"],
          ].map(([l, v, tone, tid]) => (
            <div key={l} className="rounded-lg px-4 py-3" data-testid={tid} style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.12)" }}>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{l}</div>
              <div className="text-lg font-extrabold mt-0.5" style={{ color: tone || "#fff" }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-lg overflow-x-auto" style={{ border: "1px solid rgba(123,211,206,0.12)" }}>
        <table className="text-xs w-full">
          <thead><tr style={{ backgroundColor: C.panel }}>
            <th className="px-3 py-2 text-left text-white/50 font-bold">Metric</th>
            {YEARS.map((y, i) => <th key={y} className="px-3 py-2 text-right font-bold" style={{ color: C.orange }}>{yl[i]}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(([k, label, f]) => (
              <tr key={k} className="border-t border-white/5">
                <td className="px-3 py-1.5 text-white/60 whitespace-nowrap">{label}</td>
                {YEARS.map((y) => (
                  <td key={y} className="px-3 py-1.5 text-right text-white/80 whitespace-nowrap"
                    style={k === "ltv_cac" ? { color: ratioTone(ue.years[y]?.[k]) } : undefined}>
                    {f(ue.years[y]?.[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
